<template>
  <AdminLayout>
  <div class="d-flex flex-column">
    <div class="d-flex flex-row align-center mb-3">
      <h1 class="outfit-regular">Realm Creator</h1>
      <v-spacer></v-spacer>
      <v-btn icon @click="openRealmsDialog = true" :disabled="loading" dark color="black">
        <v-icon>mdi-book-open-blank-variant</v-icon>
      </v-btn>
    </div>
    <v-progress-linear :value="progressValue" stream color="yellow darken-3" class="mt-2 mb-8"></v-progress-linear>
  
    <v-alert v-if="errorMessage" type="error" dense class="my-4" text>{{ errorMessage }}</v-alert>

    <!-- ---------------------- Realm Idea ------a---------------- -->
    <div class="d-flex flex-row align-center mb-6">
      <v-text-field
        v-model="realm.idea"
        label="Enter a creative realm idea"
        filled
        dense
        :disabled="loading"
        hide-details
      ></v-text-field>
      <v-btn v-if="!isEditMode" @click="generateRealm" :loading="loading" dark color="black" class="ml-2">
        <v-icon left>mdi-lightning-bolt</v-icon>
        Generate Realm
      </v-btn>
      <v-btn v-if="isEditMode" @click="updateRealm" :loading="saving" dark color="black" class="ml-2">
        <v-icon left>mdi-check</v-icon>
        Update Realm
      </v-btn>
    </div>

    <!-- Expansion Panels (no changes here) -->
    <v-expansion-panels accordion multiple v-model="panels">
      <v-expansion-panel>
        <v-expansion-panel-header>
          <v-icon left class="flex-none mr-4" :color="realm.name ? 'green' : 'grey'">
            {{ realm.name ? 'mdi-check-circle' : 'mdi-circle-outline' }}
          </v-icon>
          <div class="text-overline">Basic Info</div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <div class="text-overline">Name</div>
          <v-sheet color="grey lighten-4" rounded class="pa-4">
            <v-text-field solo label="Name" v-model="realm.name" hide-details :readonly="loading"></v-text-field>
          </v-sheet>
          <div class="text-overline mt-6">Concept</div>
          <v-sheet color="grey lighten-4" rounded class="pa-4">
            <v-textarea solo label="Concept" v-model="realm.concept" hide-details auto-grow :readonly="loading"></v-textarea>
          </v-sheet>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-header>
          <v-icon left class="flex-none mr-4" :color="realm.lore.short ? 'green' : 'grey'">
            {{ realm.lore.short ? 'mdi-check-circle' : 'mdi-circle-outline' }}
          </v-icon>
          <div class="text-overline">Lore</div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <div class="text-overline">Description</div>
          <v-sheet color="grey lighten-4" rounded class="pa-4">
            <v-textarea solo label="Short Lore" v-model="realm.lore.short" hide-details auto-grow :readonly="loading"></v-textarea>
            <v-textarea solo label="Detailed Lore" v-model="realm.lore.detailed" class="mt-4" hide-details auto-grow :readonly="loading"></v-textarea>
          </v-sheet>
          <div class="text-overline mt-6">History</div>
          <v-sheet color="grey lighten-4" rounded class="pa-4">
            <v-textarea solo label="History" v-model="realm.lore.history" hide-details auto-grow :readonly="loading"></v-textarea>
          </v-sheet>
          <div class="text-overline mt-6">Rules</div>
          <v-sheet color="grey lighten-4" rounded class="pa-4">
            <v-textarea solo label="Rules" v-model="realm.lore.rules" hide-details auto-grow :readonly="loading"></v-textarea>
          </v-sheet>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <!-- <v-expansion-panel> -->
        <RealmCreatorFactions :realm="realm" :loading="loading" />
      <!-- </v-expansion-panel> -->

      <v-expansion-panel>
        <v-expansion-panel-header>
          <v-icon left class="flex-none mr-4" :color="realm.landscape.image ? 'green' : 'grey'">
            {{ realm.landscape.image ? 'mdi-check-circle' : 'mdi-circle-outline' }}
          </v-icon>
          <div class="text-overline">Landscape</div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <div class="text-overline">Art</div>
          <v-sheet color="grey lighten-4" rounded class="pa-4">
            <!-- Display the main landscape image -->
            <div v-if="realm.landscape.image || realm.landscape.images.length > 0" class="mt-4">
              <div v-if="realm.landscape.image" class="mb-4">
                <v-img :src="realm.landscape.image" contain max-height="400"></v-img>
              </div>
              
              <!-- Display additional landscape images -->
              <v-row v-if="realm.landscape.images.length > 1" class="mt-4">
                <v-col v-for="(img, index) in realm.landscape.images" :key="`landscape-img-${index}`" cols="12" md="6">
                  <v-img :src="img" contain max-height="400" class="mb-2" @click="setMainImage(img)" :class="{ 'main-image-border': img === realm.landscape.image }"></v-img>
                </v-col>
              </v-row>
            </div>
            <div v-else-if="loading && realm.landscape.prompt && !realm.landscape.image" class="my-6 text-center grey--text">Generating/Uploading image...</div>
            <!-- Button to generate an additional image -->
            <div class="d-flex justify-end mt-4">
              <v-btn 
                @click="generateAdditionalLandscapeImage" 
                :loading="generatingLandscapeImage" 
                :disabled="loading || !realm.id"
                color="black" 
                small
                dark
              >
                <v-icon left>mdi-image-plus</v-icon>
                Generate Another Landscape
              </v-btn>
            </div>
          </v-sheet>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-header>
          <v-icon left class="flex-none mr-4" :color="realm.story.content ? 'green' : 'grey'">
            {{ realm.story.content ? 'mdi-check-circle' : 'mdi-circle-outline' }}
          </v-icon>
          <div class="text-overline">Story</div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <div class="text-overline">Content</div>
          <v-sheet color="grey lighten-4" rounded class="pa-4">
            <v-textarea solo label="Content" v-model="realm.story.content" hide-details auto-grow :readonly="loading"></v-textarea>
          </v-sheet>
          
          <div class="d-flex align-center mt-6">
            <div class="text-overline flex-grow-1">Audio Narration</div>
            <v-btn 
              v-if="realm.story.content && !realm.story.audio && !loading"
              @click="generateStoryAudio" 
              :loading="generatingAudio" 
              color="black" 
              small
              dark
            >
              <v-icon left>mdi-text-to-speech</v-icon>
              Generate Audio
            </v-btn>
          </div>
          
          <v-sheet v-if="realm.story.audio" color="grey lighten-4" rounded class="pa-4">
            <audio controls style="width: 100%" :src="realm.story.audio"></audio>
          </v-sheet>
          <div v-else-if="loading && realm.story.content && !realm.story.audio" class="my-6 text-center grey--text">Generating/Uploading audio...</div>
          <div v-else-if="generatingAudio" class="my-6 text-center grey--text">Generating/Uploading audio...</div>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-header>
           <v-icon left class="flex-none mr-4" :color="realm.characters.length > 0 ? 'green' : 'grey'">
            {{ realm.characters.length > 0 ? 'mdi-check-circle' : 'mdi-circle-outline' }}
          </v-icon>
          <div class="text-overline">Characters ({{ realm.characters.length }})</div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <div v-if="realm.characters.length === 0 && loading && completedSteps >= 6" class="text-center grey--text my-4">Generating characters...</div>
          
          <div class="d-flex justify-end mb-4">
            <v-btn 
              @click="customCharacterDialog = true" 
              :disabled="loading || !realm.id"
              color="yellow darken-3" 
              class="mr-2"
            >
              <v-icon left>mdi-account-edit</v-icon>
              Custom Character
            </v-btn>
            <v-btn 
              @click="generateNewCharacter" 
              :loading="generatingCharacter" 
              :disabled="loading || !realm.id"
              color="black" 
              dark
            >
              <v-icon left>mdi-account-plus</v-icon>
              Generate New Character
            </v-btn>
          </div>
          <v-expansion-panels>
            <v-expansion-panel v-for="(character, index) in realm.characters" :key="`char-${index}`">
              <v-expansion-panel-header>
                <v-avatar size="60" class="mr-4 flex-none">
                  <v-img :src="character.avatar.image" contain></v-img>
                </v-avatar>
                <div class="text-overline">{{ character.name }}</div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-sheet color="grey lighten-4" rounded class="pa-4">
            <v-text-field solo label="Name" v-model="character.name" hide-details :readonly="loading"></v-text-field>
            <v-textarea solo label="Origin" v-model="character.origin" class="mt-3" hide-details auto-grow :readonly="loading"></v-textarea>
            <v-textarea solo label="Lore" v-model="character.lore" class="mt-3" hide-details auto-grow :readonly="loading"></v-textarea>
            <v-text-field solo label="Role" v-model="character.role" class="mt-3" hide-details :readonly="loading"></v-text-field>
                  
                  <!-- Main character avatar display -->
                  <div v-if="character.avatar.image" class="mt-3">
                    <v-img :src="character.avatar.image" contain max-height="400"></v-img>
                  </div>
                  
                  <!-- Display additional character images -->
                  <v-row v-if="character.avatar.images && character.avatar.images.length > 1" class="mt-4">
                    <v-col v-for="(img, imgIndex) in character.avatar.images" :key="`char-img-${index}-${imgIndex}`" cols="12" md="6">
                      <v-img 
                        :src="img" 
                        contain 
                        max-height="300" 
                        class="mb-2" 
                        @click="setMainCharacterImage(index, img)" 
                        :class="{ 'main-image-border': img === character.avatar.image }"
                      ></v-img>
                    </v-col>
                  </v-row>
                  
                  <!-- Button to generate an additional character image -->
                  <div class="d-flex justify-end mt-4">
                    <v-btn 
                      @click="generateAdditionalCharacterImage(index)" 
                      :loading="generatingCharacterImage && generatingCharacterIndex === index" 
                      :disabled="loading || !realm.id || !character.avatar.prompt"
                      color="black" 
                      small
                      dark
                    >
                      <v-icon left>mdi-image-plus</v-icon>
                      Generate Another Avatar
                    </v-btn>
                  </div>
                  
                  <v-btn icon @click="removeCharacter(index)"><v-icon>mdi-delete</v-icon></v-btn>
          </v-sheet>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
    <v-dialog v-model="openRealmsDialog" max-width="800">
      <v-card>
        <v-card-title>Realms</v-card-title>
        <v-card-text>
          <v-list>
            <v-list-item v-for="realm in realms" :key="realm.id" @click="loadRealm(realm.id)">
              <v-list-item-avatar size="60" tile>
                <v-img :src="realm.landscape.image" contain max-height="400"></v-img>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>{{ realm.name }}</v-list-item-title>
                <v-list-item-subtitle>{{ realm.characters.length }} characters</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
      </v-card>
    </v-dialog>
    
    <v-dialog v-model="customCharacterDialog" max-width="600">
      <v-card>
        <v-card-title>Create Custom Character</v-card-title>
        <v-card-text>
          <v-alert v-if="errorMessage" type="error" dense text>{{ errorMessage }}</v-alert>
          <v-form ref="customCharacterForm" @submit.prevent="generateCustomCharacter">
            <v-text-field
              v-model="customCharacter.name"
              label="Character Name"
              :rules="[v => !!v || 'Name is required']"
              required
              outlined
            ></v-text-field>
            
            <v-textarea
              v-model="customCharacter.description"
              label="Brief Description"
              hint="Describe the character's appearance, personality, goals, or special abilities"
              :rules="[v => !!v || 'Description is required']"
              required
              auto-grow
              rows="3"
              outlined
            ></v-textarea>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="customCharacterDialog = false">Cancel</v-btn>
          <v-btn
            color="black" 
            dark
            :loading="generatingCustomCharacter"
            @click="generateCustomCharacter"
          >
            <v-icon left>mdi-account-plus</v-icon>
            Generate Character
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
  </AdminLayout>
</template>

<script>
import { collection, addDoc, doc, getDocs, updateDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { db } from '@/firebase/init';
import { getIdToken } from "firebase/auth";
import RealmCreatorFactions from '@/components/admin/RealmCreatorFactions.vue';
import { jsonrepair } from 'jsonrepair';

import { 
  getStorage,
  ref as storageFileRef, 
  uploadBytes,
  getDownloadURL,
  uploadString 
} from 'firebase/storage';


import AdminLayout from '@/layouts/Admin.vue'

export default {
  
  name: 'AdminRealms',
  
  components: {
    AdminLayout,
    RealmCreatorFactions
  },

  data() {
    return {
      realm: {
        id: null,
        idea: "",
        name: "", concept: "",
        lore: { short: "", detailed: "", history: "", rules: "" },
        factions: [],
        landscape: { 
          prompt: "", 
          image: "", // Keep for backward compatibility
          images: [] // Array of additional images
        },
        story: { content: "", audio: "" },
        characters: [
          // {
          //   name: "",
          //   origin: "",
          //   lore: "",
          //   avatar: { prompt: "", image: "", images: [] }
          // }
        ],
      },
      loading: false,
      saving: false,
      errorMessage: '',
      totalSteps: 0, completedSteps: 0, progressValue: 0,
      panels: [0], 
      NUM_CHARACTERS: 5, 
      storage: null, 
      isEditMode: false,
      generatingCharacter: false,
      generatingAudio: false,
      generatingLandscapeImage: false,
      generatingCharacterImage: false,
      generatingCharacterIndex: -1,
      openRealmsDialog: false,
      realms: [],
      customCharacterDialog: false,
      customCharacter: {
        name: "",
        role: "",
        description: "",
      },
      characterRoles: ["Hero", "Villain", "Adventurer", "Royal Advisor", "Renegade Mage", "Dragon Slayer", "Elemental Master", "Sea Captain", "Mountain Guide", "Lore Keeper", "Shadow Assassin"],
      generatingCustomCharacter: false,
    }
  },
  
  computed: {
    pageTitle() {
      return this.isEditMode ? `Edit Realm: ${this.realm.name}` : 'Realm Creator';
    }
  },

  created() {
    this.storage = getStorage(); 
    this.checkForExistingRealm();
    this.fetchRealms();
  },

  methods: {
    async fetchRealms() {
      this.loading = true;
      this.realms = [];
      const realmsRef = collection(db, 'realms');
      const querySnapshot = await getDocs(realmsRef);
      querySnapshot.forEach((doc) => {
        let realm = doc.data();
        realm.id = doc.id
        this.realms.push(realm);
      });
      this.loading = false;
    },

    loadRealm(realmId) {
      this.loading = true;
      this.panels = [];
      this.isEditMode = true;
      this.realm = this.realms.find(realm => realm.id === realmId);
      
      // Ensure required nested arrays exist
      if (!this.realm.factions) {
        this.realm.factions = [];
      }
      if (!this.realm.characters) {
        this.realm.characters = [];
      }
      if (!this.realm.landscape.images) {
        this.realm.landscape.images = [];
      }
      
      // Ensure character avatar images arrays exist
      this.realm.characters.forEach(character => {
        if (!character.avatar.images) {
          character.avatar.images = [];
        }
        // If we have a main image but empty images array, add the main image to the array
        if (character.avatar.image && character.avatar.images.length === 0) {
          character.avatar.images.push(character.avatar.image);
        }
      });
      
      this.openRealmsDialog = false;
      this.loading = false;
    },

    async checkForExistingRealm() {
      const realmId = this.$route.params.id;
      if (realmId) {
        this.loading = true;
        try {
          const realmDoc = await getDoc(doc(db, 'realms', realmId));
          if (realmDoc.exists()) {
            this.isEditMode = true;
            this.realm = { ...realmDoc.data(), id: realmDoc.id };
            
            // Ensure required nested arrays exist for older realms
            if (!this.realm.factions) {
              this.realm.factions = [];
            }
            if (!this.realm.characters) {
              this.realm.characters = [];
            }
            if (!this.realm.landscape.images) {
              this.realm.landscape.images = [];
            }
            
            // Ensure character avatar images arrays exist
            this.realm.characters.forEach(character => {
              if (!character.avatar.images) {
                character.avatar.images = [];
              }
              // If we have a main image but empty images array, add the main image to the array
              if (character.avatar.image && character.avatar.images.length === 0) {
                character.avatar.images.push(character.avatar.image);
              }
            });
            
            // Open all panels that have data
            this.panels = [];
            if (this.realm.name) this.panels.push(0);
            if (this.realm.lore.short) this.panels.push(1);
            if (this.realm.landscape.prompt) this.panels.push(2);
            if (this.realm.story.content) this.panels.push(3);
            if (this.realm.characters.length > 0) this.panels.push(4);
            
            // If no panels are open, open the first one
            if (this.panels.length === 0) this.panels = [0];
          } else {
            this.errorMessage = 'Realm not found';
          }
        } catch (error) {
          console.error('Error fetching realm:', error);
          this.errorMessage = `Error loading realm: ${error.message}`;
        } finally {
          this.loading = false;
        }
      }
    },
    
    async callHttpFunction(url, payload, loadingFlagKey, resultPropKey, errorPropKey, successCallback, errorCallback, isBinaryResponse = false) {
      if (loadingFlagKey) this[loadingFlagKey] = true;
      if (resultPropKey) this[resultPropKey] = null; 
      if (errorPropKey) this[errorPropKey] = null;   

      try {
        if (!this.authUser) throw new Error("User not authenticated for HTTP call.");
        const token = await getIdToken(this.authUser);
        
        // For audio generation, we need to specify we want binary response
        const isAudioGeneration = typeof url === 'string' && url.includes('generateAudio');
        if (isAudioGeneration) {
          console.log('Audio generation request, setting binary response flag');
          isBinaryResponse = true; 
        }
        
        const response = await fetch(url, {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`, 
            'Content-Type': 'application/json',
            'Accept': isAudioGeneration ? 'audio/mpeg' : 'application/json'
          },
          body: JSON.stringify(payload) 
        });

        if (!response.ok) {
          let errorData = { message: `HTTP error! status: ${response.status}` };
          try { 
            const errText = await response.text(); // Try to get text first for more info
            console.error("Error response text:", errText);
            const errJson = JSON.parse(errText); // Then try to parse
            errorData.message = errJson.error?.message || errJson.error || errJson.message || errorData.message;
          } catch (e) { 
            // If parsing errText fails, or if errJson parsing had an issue
            errorData.message = response.statusText || errorData.message; 
          }
          throw new Error(errorData.message);
        }

        let responseData;
        if (isBinaryResponse) {
          console.log('Handling binary response');
          responseData = await response.blob();
          if (isAudioGeneration) {
            // For audio, create a blob URL that can be used directly
            const blobUrl = URL.createObjectURL(responseData);
            // console.log('Created blob URL for audio:', blobUrl);
            responseData = blobUrl;
          }
        } else {
          const textResponse = await response.text(); 
          try { 
            responseData = JSON.parse(textResponse); 
            if (url === this.$endpoints.generateImage && responseData.b64_json) {
              
            }
          } 
          catch (e) { 
            responseData = textResponse; 
          }
        }
        
        if (resultPropKey && typeof this[resultPropKey] !== 'undefined') { this[resultPropKey] = responseData; }
        if (successCallback) successCallback(responseData);

      } catch (error) {
        console.error(`Error calling ${url}:`, error);
        if (errorPropKey) this[errorPropKey] = error.message || `Failed to call ${url}`;
        if (errorCallback) errorCallback(error);
        else throw error; 
      } finally {
        if (loadingFlagKey) this[loadingFlagKey] = false;
      }
    },

    _extractJsonFromString(text) {
      // If backend returned vendor envelope { text: string }
      if (typeof text === 'object' && text !== null) {
        if (typeof text.text === 'string') {
          text = text.text;
        } else {
          return text;
        }
      }
      if (typeof text !== 'string') throw new Error("Invalid input type for JSON extraction.");

      // Try to extract JSON from markdown code block
      const markdownMatch = text.match(/```json\s*([\s\S]*?)\s*```/im);
      let jsonString = markdownMatch ? markdownMatch[1] : text;

      // Try to find the first and last curly braces
      const firstBrace = jsonString.indexOf('{');
      const lastBrace = jsonString.lastIndexOf('}');
      if (firstBrace !== -1 && lastBrace > firstBrace) {
        jsonString = jsonString.substring(firstBrace, lastBrace + 1);
      }

      // Try to repair and parse the JSON
      try {
        return JSON.parse(jsonString);
      } catch (e) {
        try {
          return JSON.parse(jsonrepair(jsonString));
        } catch (e2) {
          console.error("Failed to parse/repair JSON. Raw output:", text);
          throw new Error("LLM response was not valid JSON. Raw: " + text.substring(0, 200) + "...");
        }
      }
    },

    removeCharacter(index) {
      this.realm.characters.splice(index, 1);
      this.updateProgress(4);
    },

    resetRealm() {
      const ideaToKeep = this.realm.idea;
      this.realm = {
        id: null, idea: ideaToKeep, name: "", concept: "",
        lore: { short: "", detailed: "", history: "", rules: "" },
        landscape: { prompt: "", image: "", images: [] },
        story: { content: "", audio: "" },
        characters: [],
      };
      this.completedSteps = 0; this.progressValue = 0; this.panels = [0];
    },

    updateProgress(autoOpenNextPanelIndex = -1) {
      this.completedSteps++;
      if (this.totalSteps > 0) {
        this.progressValue = Math.min((this.completedSteps / this.totalSteps) * 100, 100);
      }
      if (autoOpenNextPanelIndex !== -1 && autoOpenNextPanelIndex < 7 && !this.panels.includes(autoOpenNextPanelIndex)) { 
        this.panels.push(autoOpenNextPanelIndex);
      }
    },

    async _executeLlmStep(prompt, targetProperty, processFn, dbUpdateDataFn, realmDbRef, autoOpenPanelIndex) {
      if (!this.authUser) throw new Error("User not authenticated");
      if (!this.realm.id) throw new Error("No realm ID");

      this.errorMessage = '';

      const baseInstruction = `You are an AI assistant that STRICTLY outputs JSON.\nDo NOT include any Markdown formatting (like \`\`\`json ... \`\`\`), any explanatory text, comments, or any conversational fluff before or after the JSON object.\nThe entire response MUST be a single, valid JSON object.`;

      const maxAttempts = 3;
      let lastError = null;
      const providerOrder = ['anthropic', 'google', 'openai'];

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          const attemptReminder = attempt > 1
            ? `\n\nReminder: Your previous response failed validation (missing keys or invalid JSON). Correct it. Return ONLY a single valid JSON object that exactly matches the requested schema.`
            : '';

          const fullPrompt = `${baseInstruction}\n\n${prompt}${attemptReminder}`;
          const provider = providerOrder[Math.min(attempt - 1, providerOrder.length - 1)];

          let rawLlmResponse = null;
          await this.callHttpFunction(
            this.$endpoints.invokeLLM,
            { provider, tier: 'fast', stream: false, prompt: fullPrompt },
            null, null, null,
            (data) => { rawLlmResponse = data; },
            (error) => { throw new Error(`LLM call failed: ${error.message}`); }
          );

          if (rawLlmResponse === null) throw new Error('No data received from LLM.');

          const jsonData = this._extractJsonFromString(rawLlmResponse);
          // Will throw if invalid/missing keys
          processFn(jsonData);

          const updatePayload = dbUpdateDataFn();
          await updateDoc(realmDbRef, { ...updatePayload, updatedAt: serverTimestamp() });
          this.updateProgress(autoOpenPanelIndex);
          return; // success
        } catch (err) {
          lastError = err;
          console.warn(`Attempt ${attempt} failed for ${targetProperty}:`, err?.message || err);
          if (attempt < maxAttempts) {
            // small delay between retries
            await new Promise((r) => setTimeout(r, 300));
            continue;
          }
        }
      }

      const errorMessage = lastError?.message || 'Unknown error';
      console.error(`Error generating ${targetProperty} after retries: ${errorMessage}`);
      this.errorMessage = `Failed while generating ${targetProperty}: ${errorMessage}`;
      throw lastError || new Error('Unknown LLM error');
    },

    async _executeImageStep(promptText, imageFileNameSeed, imageTargetSetterFn, dbUpdateDataFn, realmDbRef, autoOpenPanelIndex) {
      const description = imageFileNameSeed; // Use imageFileNameSeed as description for logging
      // console.log(`Executing Image Step: ${description} (prompt: "${promptText}")`);
      
      this.errorMessage = '';

      if (!promptText || promptText.trim() === "") {
        console.warn(`Skipping image generation for ${description} due to empty prompt.`);
        this.updateProgress(autoOpenPanelIndex);
        return;
      }

      if (!this.realm.id || !this.authUser) {
        console.error(`Cannot save image for ${description}: Realm ID or user UID missing.`);
        this.updateProgress(autoOpenPanelIndex);
        return;
      }

      // Use the prompt directly without adding style prefixes
      const finalPrompt = promptText.trim();
      // console.log(`Using prompt for ${description}: "${finalPrompt}"`);

      let generatedImageData = null;
      try {
        await this.callHttpFunction(
          this.$endpoints.generateImage,
          { prompt: finalPrompt },
          null,
          null,
          null,
          (data) => { generatedImageData = data; }
        );
      } catch (genError) {
        console.error(`Image generation API call for ${description} failed: ${genError.message}`);
      }

      let finalStorageUrl = null;
      if (generatedImageData) {
        try {
          const imageFileExtension = 'png';
          const imageName = `${imageFileNameSeed.toLowerCase().replace(/\s+/g, '_')}_${Date.now()}.${imageFileExtension}`;
          const filePath = `public/realms/${this.realm.id}/${imageName}`;
          const imageStorageRef = storageFileRef(this.storage, filePath);

          let uploadResult;
          if (typeof generatedImageData === 'string' && generatedImageData.startsWith('data:image')) {
            uploadResult = await uploadString(imageStorageRef, generatedImageData, 'data_url');
          } else if (typeof generatedImageData === 'object' && generatedImageData.b64_json) {
            const dataUrl = `data:image/png;base64,${generatedImageData.b64_json}`;
            uploadResult = await uploadString(imageStorageRef, dataUrl, 'data_url');
          } else if (typeof generatedImageData === 'string') {
            const response = await fetch(generatedImageData);
            if (!response.ok) throw new Error(`Failed to fetch image from URL: ${response.statusText}`);
            const blob = await response.blob();
            uploadResult = await uploadBytes(imageStorageRef, blob);
          } else {
            throw new Error('Unsupported image data format.');
          }

          finalStorageUrl = await getDownloadURL(uploadResult.ref);
        } catch (uploadError) {
          console.error(`Failed to upload/get URL for ${description} image:`, uploadError);
        }
      }

      imageTargetSetterFn(finalStorageUrl || "");

      if (finalStorageUrl) {
        const updatePayload = dbUpdateDataFn();
        await updateDoc(realmDbRef, {
          ...updatePayload,
          updatedAt: serverTimestamp(),
        });
      }

      this.updateProgress(autoOpenPanelIndex);
      // console.log(`Completed Image Step: ${description} (Final URL: ${finalStorageUrl || 'N/A'})`);
    },

    async _executeAudioStep(text, description, audioFileNameSeed, audioTargetSetterFn, dbUpdateDataFn, realmDbRef, autoOpenPanelIndex) {
      // console.log(`Executing Audio Step: ${description} (text length: ${text.length})`);
      this.errorMessage = '';

      if (!text || text.trim() === "") {
        console.warn(`Skipping audio generation for ${description} due to empty text.`);
        this.updateProgress(autoOpenPanelIndex);
        return;
      }

      if (!this.realm.id || !this.authUser) {
        console.error(`Cannot save audio for ${description}: Realm ID or user UID missing.`);
        this.updateProgress(autoOpenPanelIndex);
        return;
      }

      // Ensure TTS endpoint exists
      const ttsUrl = this.$endpoints && this.$endpoints.generateAudio;
      if (!ttsUrl || typeof ttsUrl !== 'string') {
        console.warn('TTS endpoint ($endpoints.generateAudio) is not configured. Skipping audio generation.');
        audioTargetSetterFn("");
        // Persist empty audio field if desired
        const updatePayload = dbUpdateDataFn();
        if (updatePayload && Object.keys(updatePayload).length > 0) {
          try { await updateDoc(realmDbRef, { ...updatePayload, updatedAt: serverTimestamp() }); } catch (e) {}
        }
        this.updateProgress(autoOpenPanelIndex);
        return;
      }

      let generatedAudioData = null;
      try {
        await this.callHttpFunction(
          ttsUrl,
          { input: text },
          null,
          null,
          null,
          (data) => { 
            generatedAudioData = data;
            // console.log(`Audio data received, type:`, typeof generatedAudioData);
          }
        );
      } catch (genError) {
        console.error(`Audio generation API call for ${description} failed: ${genError.message}`);
        this.errorMessage = `Failed to generate audio: ${genError.message}`;
        this.updateProgress(autoOpenPanelIndex);
        return;
      }

      let finalStorageUrl = null;
      if (generatedAudioData) {
        try {
          // If we have a blob URL from callHttpFunction, use it to get the blob
          if (typeof generatedAudioData === 'string' && generatedAudioData.startsWith('blob:')) {
            // console.log('Using blob URL directly:', generatedAudioData.substring(0, 30) + '...');
            
            try {
              // Fetch the blob from the blob URL
              const response = await fetch(generatedAudioData);
              if (!response.ok) {
                throw new Error(`Failed to fetch from blob URL: ${response.statusText}`);
              }
              
              const blob = await response.blob();
              // console.log('Audio blob size:', blob.size);
              
              // Upload to Firebase Storage
              const audioFileExtension = 'mp3';
              const audioName = `${audioFileNameSeed.toLowerCase().replace(/\s+/g, '_')}_${Date.now()}.${audioFileExtension}`;
              const filePath = `public/realms/${this.realm.id}/${audioName}`;
              const audioStorageRef = storageFileRef(this.storage, filePath);
              
              const uploadResult = await uploadBytes(audioStorageRef, blob);
              finalStorageUrl = await getDownloadURL(uploadResult.ref);
              // console.log('Firebase upload complete, URL:', finalStorageUrl.substring(0, 50) + '...');
            } catch (blobError) {
              console.error('Error handling blob URL:', blobError);
              throw blobError;
            }
          } else {
            console.error('Unexpected audio data format:', typeof generatedAudioData);
            throw new Error('Audio data is not in the expected blob URL format');
          }
        } catch (uploadError) {
          console.error(`Failed to upload/get URL for ${description} audio:`, uploadError);
          this.errorMessage = `Failed to upload audio: ${uploadError.message}`;
        }
      } else {
        console.warn('No audio data generated');
        this.errorMessage = 'No audio data was generated';
      }

      // Set the URL in the realm object
      audioTargetSetterFn(finalStorageUrl || "");

      // Update Firebase if we have a URL
      if (finalStorageUrl) {
        const updatePayload = dbUpdateDataFn();
        await updateDoc(realmDbRef, {
          ...updatePayload,
          updatedAt: serverTimestamp(),
        });
      }

      this.updateProgress(autoOpenPanelIndex);
      // console.log(`Completed Audio Step: ${description} (Final URL: ${finalStorageUrl ? 'success' : 'N/A'})`);
    },

    async generateRealm() {
      if (!this.authUser) {
        this.errorMessage = "You must be logged in to generate a realm."; return;
      }
      this.loading = true; this.errorMessage = '';
      const currentIdea = this.realm.idea;
      if (!currentIdea || currentIdea.trim() === "") {
        this.errorMessage = "Please enter a realm idea first."; this.loading = false; return;
      }

      let realmDbRef;

      try {
        if (this.isEditMode && this.realm.id) {
          // Update existing realm
          // console.log(`Updating existing realm with ID: ${this.realm.id}`);
          realmDbRef = doc(db, 'realms', this.realm.id);
          await updateDoc(realmDbRef, {
            idea: currentIdea,
            updatedAt: serverTimestamp()
          });
        } else {
          // Create new realm
          this.resetRealm(); 
          this.realm.idea = currentIdea;
          console.log("Step 0: Initializing Realm in Firestore...");
          const docRef = await addDoc(collection(db, 'realms'), {
            ...this.realm, userId: this.authUser.uid,
            createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
          });
          this.realm.id = docRef.id;
          realmDbRef = doc(db, 'realms', this.realm.id);
        }

        this.totalSteps = 1 + 1 + 1 + 1 + 1 + 1 + (this.NUM_CHARACTERS * 2);
        this.updateProgress(1); 
        // console.log(`Realm ${this.isEditMode ? 'updated' : 'initialized'} with ID: ${this.realm.id}`);

        // Step 1: Name and Concept
        const basicPrompt = `
        You are generating a new fantasy realm based on the idea: "${this.realm.idea}".

        Rules:
        - The name must be two words and follow the pattern "[SingleWord] Realm". It should evoke a mood or atmosphere, e.g., Emberfall Realm, Frostshade Realm, Thornwild Realm.
        - The concept must be at least 3 paragraphs long.
        - Describe key factions, geography, magical systems, major threats, and tone.
        - Make the world feel ancient, immersive, and alive — like a place someone could explore for years.       

        Output JSON in this format:

        {
          "name": "string",
          "concept": "string"
        }
        `;
        await this._executeLlmStep(basicPrompt, "Name and Concept",
          (data) => {
            if (!data.name || typeof data.name !== 'string' || !data.concept || typeof data.concept !== 'string') throw new Error("Name/Concept JSON missing/invalid keys.");
            this.realm.name = data.name; this.realm.concept = data.concept;
          },
          () => ({ name: this.realm.name, concept: this.realm.concept }), realmDbRef, 1);

        // Step 2: Lore
        const lorePrompt = `
        For the realm "${this.realm.name}", with the following concept:

        ${this.realm.concept}

        Write the following fields in JSON:

        {
          "short_lore": "A 1-paragraph summary that reads like the back of a fantasy novel",
          "detailed_lore": "at least 4 paragraphs giving rich detail about the history, power structures, gods, magic, and mysteries of the realm",
          "history": "Chronological summary of major ages and turning points",
          "rules": "A numbered list (minimum 5) explaining how the world works — magic systems, physics, life/death, gods, prophecies, technology, etc."
        }
        `;
        await this._executeLlmStep(lorePrompt, "Lore",
          (data) => {
            if (!data.short_lore || !data.detailed_lore || !data.history || !data.rules) throw new Error("Lore JSON missing keys.");
            this.realm.lore = { short: data.short_lore, detailed: data.detailed_lore, history: data.history, rules: data.rules };
          },
          () => ({ lore: this.realm.lore }), realmDbRef, 2);

        // Step 3: Landscape Description & Image Prompt
        // const landscapePrompt = `
        // For the realm "${this.realm.name}", with the concept ${this.realm.concept}.

        // Output JSON in this format:
        // {
        //   "image_prompt": "an image prompt including the style and composition"
        // }
        // `;
        // await this._executeLlmStep(landscapePrompt, "Landscape",
        //   (data) => {
        //     if (!data.image_prompt) throw new Error("Landscape JSON missing keys.");
        //     this.realm.landscape.prompt = data.image_prompt;
        //   },
        //   () => ({ 'landscape.prompt': this.realm.landscape.prompt }), realmDbRef, 3);

        const landscapePrompt = `
        Generate a landscape image that will be used as the background for the realm called "${this.realm.name}" based on this description: ${this.realm.lore.short}.

        Style: A digital illustration in a storybook-style featuring a magical landscape in line with the description. The scene should have soft brush textures, warm and atmospheric lighting, and the look of a vintage children's book. The composition should use a widescreen cinematic aspect ratio. Add subtle storytelling elements from the concept. The mood should be wondrous and timeless, with gently shifting weather appropriate to the concept. Avoid cartoon, hyper-stylized rendering, overly-dark, and overly-realistic. Avoid Prioritize painterly textures, natural colors, and a sense of quiet adventure. Remember this is for a children's fantasy story. 
        
        Aspect Ratio: 16:9
        `
        
        // Step 3b: Landscape Image
        await this._executeImageStep(
          landscapePrompt,
          "landscape",
          (url) => { 
            this.realm.landscape.image = url === null ? "" : url; 
            // Also add to images array if there's a valid URL
            if (url) {
              this.realm.landscape.images = this.realm.landscape.images || [];
              this.realm.landscape.images.push(url);
            }
          },
          () => ({ 
            'landscape.image': this.realm.landscape.image,
            'landscape.images': this.realm.landscape.images 
          }), 
          realmDbRef, 
          3
        );

        // Step 4: Story
        const storyPrompt = `
        Write a 10-paragraph fantasy short story set in the "${this.realm.name}".

        Rules:
        - Introduce at least 3 main protagonists and 2 antagonists in action, not as a summary.
        - Use immersive descriptions, dialogue, conflict, and magic.
        - Include a specific location from the landscape.
        - Use proper pacing, setup, escalation, and resolution (or cliffhanger).
        - Tone should match a fantasy novel: elegant, mysterious, serious but wonder-filled.
        - End on a strong moment of transformation, revelation, or power shift.
        - The story should be kid friendly and not scary.

        Lore:
        ${this.realm.lore.short}

        Landscape:
        ${this.realm.landscape.prompt}

        Output JSON schema: {"story_content": "string", "characters": [{"name": "string", "type": "string", "description": "string"}]}
        `;
        await this._executeLlmStep(storyPrompt, "Story",
          (data) => {
            if (!data.story_content) throw new Error("Story JSON missing content key.");
            this.realm.story.content = data.story_content;
            
            // Process characters if available
            if (data.characters && Array.isArray(data.characters)) {
              // console.log("Story characters detected:", data.characters);
              
              // Option: Pre-populate characters from story if needed
              // data.characters.forEach(char => {
              //   if (char.name && char.type) {
              //     this.realm.characters.push({
              //       name: char.name,
              //       role: char.type,
              //       origin: char.description || "",
              //       lore: "",
              //       avatar: { prompt: "", image: "" }
              //     });
              //   }
              // });
            }
          },
          () => ({ 'story.content': this.realm.story.content }), realmDbRef, 4);

        // Generate story audio
        await this._executeAudioStep(this.realm.story.content, "Story Audio", "story_narration",
          (url) => { this.realm.story.audio = url === null ? "" : url; },
          () => ({ 'story.audio': this.realm.story.audio }), realmDbRef, 4);

        // Step 5: Characters
        this.realm.characters = [];
        for (let i = 0; i < this.NUM_CHARACTERS; i++) {
          try {
            console.log(`Generating character ${i + 1} of ${this.NUM_CHARACTERS}`);
            await this.generateNewCharacter(realmDbRef);
            // No need to call updateProgress here as generateNewCharacter already does it
          } catch (charError) {
            console.error(`Error generating character ${i + 1}:`, charError);
            // Continue with next character if one fails
          }
        }
        
        this.errorMessage = '';
        console.log('Realm generation completed!');
        this.progressValue = 100;
        this.panels = [0,1,2,3,4];
        
      } catch (error) {
        console.error('Error generating realm:', error);
        this.errorMessage = `Failed to generate realm: ${error.message}`;
      } finally {
        this.loading = false;
      }
    },

    setMainImage(imageUrl) {
      this.realm.landscape.image = imageUrl;
    },

    setMainCharacterImage(characterIndex, imageUrl) {
      this.realm.characters[characterIndex].avatar.image = imageUrl;
    },

    getRandomCharacterType() {
      // Generate a random number between 0 and 100
      const rand = Math.floor(Math.random() * 100);
      
      // 50% male, 25% female, 25% abstract (creatures, robots, etc.)
      if (rand < 50) {
        return "male";
      } else if (rand < 75) {
        return "female";
      } else {
        return "abstract";
      }
    },

    async generateAdditionalCharacterImage(characterIndex) {
      if (!this.authUser) {
        this.errorMessage = "You must be logged in to generate an additional character image.";
        return;
      }
      
      if (!this.realm.id) {
        this.errorMessage = "No realm ID found for generating an additional character image.";
        return;
      }
      
      const character = this.realm.characters[characterIndex];
      if (!character || !character.avatar.prompt) {
        this.errorMessage = "No character prompt found to generate an additional image from.";
        return;
      }
      
      this.generatingCharacterImage = true;
      this.generatingCharacterIndex = characterIndex;
      this.errorMessage = '';
      
      try {
        // console.log(`Starting additional character image generation for ${character.name}`);
        
        // Create avatar prompt with character details
        const avatarPrompt = character.avatar.prompt;
        
        // Generate an additional character image
        await this._executeImageStep(
          avatarPrompt,
          `${character.name}_additional`,
          (url) => {
            if (url) {
              // Initialize images array if needed
              if (!character.avatar.images) {
                character.avatar.images = [];
              }
              // Add new image to array
              character.avatar.images.push(url);
              // If this is the first image, also set it as the main image
              if (!character.avatar.image) {
                character.avatar.image = url;
              }
            }
          },
          () => ({ characters: this.realm.characters }), 
          doc(db, 'realms', this.realm.id), 
          5
        );
      } catch (error) {
        console.error('Error generating additional character image:', error);
        this.errorMessage = `Failed to generate additional character image: ${error.message}`;
      } finally {
        this.generatingCharacterImage = false;
        this.generatingCharacterIndex = -1;
      }
    },

    async updateRealm() {
      if (!this.authUser) {
        this.errorMessage = "You must be logged in to update a realm.";
        return;
      }
      
      if (!this.realm.id) {
        this.errorMessage = "No realm ID found for update.";
        return;
      }
      
      this.saving = true;
      this.errorMessage = '';
      
      try {
        const realmDbRef = doc(db, 'realms', this.realm.id);
        
        // Create an update payload with all the realm data
        const updatePayload = {
          idea: this.realm.idea,
          name: this.realm.name,
          concept: this.realm.concept,
          lore: this.realm.lore,
          landscape: this.realm.landscape,
          story: this.realm.story,
          characters: this.realm.characters,
          updatedAt: serverTimestamp()
        };
        
        await updateDoc(realmDbRef, updatePayload);
        
        // Show success message briefly
        this.errorMessage = '';
        this.$nextTick(() => {
          // Use Vuetify snackbar via global event bus if available
          if (this.$root && typeof this.$root.$emit === 'function') {
            this.$root.$emit('show-snackbar', 'Realm updated successfully', 'success');
          } else {
            alert('Realm updated successfully');
          }
        });
        
      } catch (error) {
        console.error('Error updating realm:', error);
        this.errorMessage = `Failed to update realm: ${error.message}`;
      } finally {
        this.saving = false;
      }
    },

    async generateNewCharacter(customRealmDbRef = null) {
      // Ignore if customRealmDbRef is a DOM event (from button click)
      if (customRealmDbRef && customRealmDbRef.type === 'click') {
        customRealmDbRef = null;
      }
      
      if (!this.authUser) {
        this.errorMessage = "You must be logged in to generate a new character.";
        return;
      }
      
      if (!this.realm.id) {
        this.errorMessage = "No realm ID found for generating a new character.";
        return;
      }
      
      this.generatingCharacter = true;
      this.errorMessage = '';
      
      try {
        // Get existing character names for checking duplicates
        const existingNames = this.realm.characters.map(char => char.name.toLowerCase());
        
        // Get a random character type for diversity
        const characterType = this.getRandomCharacterType();
        // console.log(`Generating new character of type: ${characterType}`);
        
        const charPrompt = `
        You are generating a new character for realm "${this.realm.name}" with this concept: ${this.realm.concept}

        This character will be used as an option for the user to choose from when they play the game.
        
        The character should be generic enough to be a good fit for the realm, but should also have a unique personality and backstory.

        IMPORTANT: 
        This character should be a ${characterType} character. If the character type is an abstract entity like a robot or creature, make sure their characteristics, abilities, and appearance align with that type.

Example generic characters: 
- Frost Dragon: A powerful ice-breathing dragon who rules over frozen mountain peaks.
- Crimson Knight: A noble warrior in red armor who defends the innocent with unwavering courage.
- Shadow Thief: A stealthy burglar who moves unseen through the darkest alleys of the city.
- Storm Caller: A mage who commands weather magic and can summon lightning from clear skies.
- Iron Golem: A mechanical guardian made of enchanted metal who protects ancient ruins.
- Void Walker: A mysterious entity that travels between dimensions seeking lost knowledge.
- Golden Lion: A majestic beast companion with a noble heart and fierce loyalty.
- Blood Oracle: A seer who reads the future in crimson visions and speaks in riddles.

Output JSON schema: 
{
  "name": "Generic character name (always only 2 words). Not the actual name of the character but their alias. The second word should relate meaningfully to the character type, abilities, or role - avoid generic terms like 'keeper', 'master', 'weaver', 'sage', 'guardian', 'sentinel' unless they specifically fit the character's actual function.",
  "origin": "The origin story of the character",
  "lore": "details on their key stories, adventures, powers/abilities, character traits, and place in the realm",
  "role": "The character's role in the realm - can be hero, villain, or a specific role like 'Royal Advisor', 'Renegade Mage'",
  "avatar_prompt": "A stylized digital illustration in a textured storybook style, featuring a ${characterType} character from the shoulders up, centered inside the frame. The character has simplified, expressive features with clear outlines, storybook proportions, and a warm personality. Kid friendly. Clothing should reflect their role in ${this.realm.name}, drawn in a bold, painterly style with soft shading and visible brush textures. The color palette should be warm and harmonious. No photographic realism keep it simple. If the character is human they should have light skin and fair features, unless it doesn't make sense for the character. Dimensions 1024 x 1024 with no overflow, padding, margins, or text."
}

        Output JSON schema: 
        {
          "name": "Generic character name (always only 2 words). Not the actual name of the character but their alias.",
          "origin": "The origin story of the character",
          "lore": "details on their key stories, adventures, powers/abilities, character traits, and place in the realm",
          "role": "The character's role in the realm - can be hero, villain, or a specific role like 'Royal Advisor', 'Renegade Mage'",
          "avatar_prompt": "A stylized digital illustration in a textured storybook style, featuring a ${characterType} character from the shoulders up, centered inside the frame. The character has simplified, expressive features with clear outlines, storybook proportions, and a warm personality. Kid friendly. Clothing should reflect their role in ${this.realm.name}, drawn in a bold, painterly style with soft shading and visible brush textures. The color palette should be warm and harmonious. No photographic realism keep it simple. If the character is human they should have light skin and fair features, unless it doesn't make sense for the character. Dimensions 1024 x 1024 with no overflow, padding, margins, or text."
        }`;
        
        // Use provided realmDbRef if available, otherwise create a new one
        const realmDbRef = customRealmDbRef || doc(db, 'realms', this.realm.id);
        
          let charIndex = -1;
        let charNameForFile = `character_${this.realm.characters.length + 1}`; // Default filename seed
        await this._executeLlmStep(charPrompt, "Character",
            (data) => {
            if (!data.name || !data.origin || !data.lore || !data.avatar_prompt) throw new Error(`Character ${this.realm.characters.length + 1} JSON missing keys.`);
            
            // Check if the generated name is a duplicate
            const newCharName = data.name.trim();
            const newCharNameLower = newCharName.toLowerCase();
            
            if (existingNames.includes(newCharNameLower)) {
              // If duplicate, add a suffix to make it unique
              const suffix = Math.floor(Math.random() * 999) + 1;
              data.name = `${newCharName} ${suffix}`;
              console.warn(`Renamed duplicate character to: ${data.name}`);
            }
            
            charNameForFile = data.name || `character_${this.realm.characters.length + 1}`;
            const role = data.role || (data.name.includes(" ") ? data.name.split(" ")[0] : "Adventurer");
            this.realm.characters.push({ 
              name: data.name, 
              origin: data.origin, 
              lore: data.lore, 
              role: role, 
              avatar: { prompt: data.avatar_prompt, image: "", images: [] }
            });
              charIndex = this.realm.characters.length -1;
          }, () => ({ characters: this.realm.characters }), realmDbRef, 5);
          
          if (charIndex !== -1 && this.realm.characters[charIndex].avatar.prompt) {
          await this._executeImageStep(
            this.realm.characters[charIndex].avatar.prompt,
            charNameForFile,
            (url) => { 
              this.realm.characters[charIndex].avatar.image = url === null ? "" : url;
              // Also add to images array if there's a valid URL
              if (url) {
                this.realm.characters[charIndex].avatar.images = this.realm.characters[charIndex].avatar.images || [];
                this.realm.characters[charIndex].avatar.images.push(url);
              }
            },
            () => ({ characters: this.realm.characters }), 
            realmDbRef, 
            5
          );
          } else { 
          console.warn(`Skipping image for Character ${this.realm.characters.length} due to missing prompt or index.`);
          this.updateProgress(5); 
        }
      } catch (error) {
        console.error('Error generating new character:', error);
        this.errorMessage = `Failed to generate new character: ${error.message}`;
      } finally {
        this.generatingCharacter = false;
      }
    },

    async generateStoryAudio() {
      if (!this.authUser) {
        this.errorMessage = "You must be logged in to generate story audio.";
        return;
      }
      
      if (!this.realm.id) {
        this.errorMessage = "No realm ID found for generating story audio.";
        return;
      }
      
      if (!this.realm.story.content) {
        this.errorMessage = "No story content found to generate audio from.";
        return;
      }
      
      this.generatingAudio = true;
      this.errorMessage = '';
      
      try {
        console.log("Starting audio generation for story");
        // Generate story audio directly from existing content
        await this._executeAudioStep(this.realm.story.content, "Story Audio", "story_narration",
          (url) => { this.realm.story.audio = url === null ? "" : url; },
          () => ({ 'story.audio': this.realm.story.audio }), doc(db, 'realms', this.realm.id), 4);
      } catch (error) {
        console.error('Error generating story audio:', error);
        this.errorMessage = `Failed to generate story audio: ${error.message}`;
      } finally {
        this.generatingAudio = false;
      }
    },

    async generateAdditionalLandscapeImage() {
      if (!this.authUser) {
        this.errorMessage = "You must be logged in to generate an additional landscape image.";
        return;
      }
      
      if (!this.realm.id) {
        this.errorMessage = "No realm ID found for generating an additional landscape image.";
        return;
      }
      
      this.generatingLandscapeImage = true;
      this.errorMessage = '';
      
      try {
        console.log("Starting additional landscape image generation");
        
        const landscapePrompt = `
        Generate a landscape image that will be used as the background for the realm called "${this.realm.name}" based on this description: ${this.realm.lore.short}.

        Style: A digital illustration in a storybook-style featuring a magical landscape in line with the description. The scene should have soft brush textures, warm and atmospheric lighting, and the look of a vintage children's book. The composition should use a widescreen cinematic aspect ratio. Add subtle storytelling elements from the concept. The mood should be wondrous and timeless, with gently shifting weather appropriate to the concept. Avoid cartoon, hyper-stylized rendering, overly-dark, and overly-realistic. Avoid Prioritize painterly textures, natural colors, and a sense of quiet adventure. Remember this is for a children's fantasy story. 
        
        Aspect Ratio: 16:9
        `;

        // Generate an additional landscape image
        await this._executeImageStep(
          landscapePrompt,
          "additional_landscape",
          (url) => {
            if (url) {
              this.realm.landscape.images.push(url);
            }
          },
          () => ({ 'landscape.images': this.realm.landscape.images }), 
          doc(db, 'realms', this.realm.id), 
          3
        );
      } catch (error) {
        console.error('Error generating additional landscape image:', error);
        this.errorMessage = `Failed to generate additional landscape image: ${error.message}`;
      } finally {
        this.generatingLandscapeImage = false;
      }
    },

    async generateCustomCharacter() {
      if (!this.$refs.customCharacterForm.validate()) {
        return;
      }

      if (!this.authUser) {
        this.errorMessage = "You must be logged in to generate a custom character.";
        return;
      }
      
      if (!this.realm.id) {
        this.errorMessage = "No realm ID found for generating a custom character.";
        return;
      }
      
      this.generatingCustomCharacter = true;
      this.errorMessage = '';
      
      try {
        console.log("Starting custom character generation with name:", this.customCharacter.name);
        
        const charPrompt = `
        You are generating a new character for realm "${this.realm.name}" with this concept: ${this.realm.concept}

        The user has specified the following details for this character:
        - Name: ${this.customCharacter.name}
        - Description: ${this.customCharacter.description}

        Create a rich, detailed character based on these specifications that fits well within the realm.
        
        Output JSON schema: 
        {
          "name": "${this.customCharacter.name}",
          "origin": "A detailed origin story for the character, incorporating elements from the user's description",
          "lore": "Detailed lore about the character's adventures, powers, abilities, and place in the realm. Incorporate elements from the user's description and expand upon them to create a rich narrative.",
          "role": "Determine the role of the character based on their origin, lore, and the user's description. The role should be a specific role like 'Hero', 'Villain', 'Adventurer', 'Royal Advisor', 'Renegade Mage', 'Dragon Slayer', 'Elemental Master', 'Sea Captain', 'Mountain Guide', 'Lore Keeper', 'Shadow Assassin', etc.",
          "avatar_prompt": "A stylized digital illustration in a textured storybook style, featuring ${this.customCharacter.name} from the shoulders up, centered inside the frame. The character should be depicted as ${this.customCharacter.description}. The character has simplified, expressive features with clear outlines, storybook proportions, and a warm personality. Kid friendly. Clothing should reflect their role as ${this.customCharacter.role} in ${this.realm.name}, drawn in a bold, painterly style with soft shading and visible brush textures. The color palette should be warm and harmonious. No photographic realism keep it simple. If the character is human they should have light skin and fair features, unless it doesn't make sense for the character. Dimensions 1024 x 1024 with no overflow, padding, or margins."
        }`;
        
        let charIndex = -1;
        let charNameForFile = this.customCharacter.name.replace(/\s+/g, '_').toLowerCase();
        
        const realmDbRef = doc(db, 'realms', this.realm.id);
        
        await this._executeLlmStep(charPrompt, "Custom Character",
          (data) => {
            if (!data.origin || !data.lore || !data.avatar_prompt) {
              throw new Error(`Custom character JSON missing required fields`);
            }
            
            // Use the name from the form, not from the LLM
            const name = this.customCharacter.name;
            const role = this.customCharacter.role;
            
            this.realm.characters.push({ 
              name: name, 
              origin: data.origin, 
              lore: data.lore, 
              role: role, 
              avatar: { prompt: data.avatar_prompt, image: "", images: [] }
            });
            charIndex = this.realm.characters.length - 1;
          }, 
          () => ({ characters: this.realm.characters }), 
          realmDbRef, 
          5
        );
        
        if (charIndex !== -1 && this.realm.characters[charIndex].avatar.prompt) {
          await this._executeImageStep(
            this.realm.characters[charIndex].avatar.prompt,
            charNameForFile,
            (url) => { 
              this.realm.characters[charIndex].avatar.image = url === null ? "" : url;
              // Also add to images array if there's a valid URL
              if (url) {
                this.realm.characters[charIndex].avatar.images = this.realm.characters[charIndex].avatar.images || [];
                this.realm.characters[charIndex].avatar.images.push(url);
              }
            },
            () => ({ characters: this.realm.characters }), 
            realmDbRef, 
            5
          );
        } else { 
          console.warn(`Skipping image for Custom Character due to missing prompt or index.`);
          this.updateProgress(5); 
        }
        
        // Reset form and close dialog after successful generation
        this.customCharacter = { name: "", role: "", description: "" };
        this.customCharacterDialog = false;
        
      } catch (error) {
        console.error('Error generating custom character:', error);
        this.errorMessage = `Failed to generate custom character: ${error.message}`;
      } finally {
        this.generatingCustomCharacter = false;
      }
    },
  }
}
</script>

<style lang="scss" scoped>
.main-image-border {
  border: 2px solid #42A5F5;
}
</style>